
import { Box, Button, FormControl, FormLabel, FormHelperText, Input, ModalBody, ModalFooter, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CollecticonPlusSmall } from "@devseed-ui/collecticons-chakra";

import DropZone from "./DropZone";
import FileList from "./FileList";
import { Scene } from "../../../../types/uploads";
import { sha256 } from "../../../../utils";
import { MAX_FILE_SIZE, ACCEPTED_TYPES, MIN_DATE, MAX_DATE } from "../../../../settings";
import API from "../../../../api";

function validateFile(file?: File): string {
  if (!file) return "";

  if (!ACCEPTED_TYPES.includes(file.type)) {
    return "The uploaded file is not accepted. Select a TIFF.";
  }

  if (file.size > MAX_FILE_SIZE) {
    return "The file size exceeds the limit of 20GB. Upload a smaller file.";
  }

  return "";
}

function getDateFromFileName(fileName: string) {
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const dateRegex = new RegExp(`^(\\d{2})(${months.join("|")})(0[1-9]|[12][0-9]|3[01])([01][1-9]|2[1-3])([0-5][0-9])([0-5][0-9])`);
  const match = fileName.match(dateRegex);

  if (match) {
    const [y, _, d, hh, mm, ss] = match.slice(1, 7).map((n) => parseInt(n, 10));  // eslint-disable-line @typescript-eslint/no-unused-vars
    const utcDate = new Date(Date.UTC(y + 2000, months.indexOf(match[2]), d, hh, mm, ss));
    return utcDate.toISOString();
  }

  return "";
}

type Props = {
  files?: Scene[];
  onSelect: (files: Scene) => void;
  onNext: () => void;
  onCancel: () => void;
}

function SelectFiles({ files, onSelect, onNext, onCancel }: Props) {
  const [ panchromatic, setPanchromatic ] = useState<File>();
  const [ multiSpectral, setMultiSpectral ] = useState<File>();
  const [ isSelecting, setIsSelecting ] = useState<boolean>(true);
  const [ date, setDate ] = useState<string>("");
  const [ error, setError ] = useState<string>();

  const panchromaticError = validateFile(panchromatic);
  const multiSpectralError = validateFile(multiSpectral);

  const handleNext = () => {
    if (!(panchromatic && multiSpectral)) {
      setError("Select one panchromatic and one multispectral image.");
      return;
    }

    if (!date) {
      setError("Unable to extract date from file. Please set the date manually.");
      return;
    }
    onNext();
  };

  useEffect(() => {
    // Try to extract the date from the file name, ensuring the dates of both files match
    if (panchromatic && multiSpectral) {
      const panchromaticFileDate = panchromatic && getDateFromFileName(panchromatic.name);
      const multiSpectralFileDate = multiSpectral && getDateFromFileName(multiSpectral.name);

      if (panchromaticFileDate && multiSpectralFileDate && panchromaticFileDate === multiSpectralFileDate) {
        setDate(panchromaticFileDate);
      }
    }
  }, [panchromatic, multiSpectral, panchromaticError, multiSpectralError, onSelect]);

  useEffect(() => {
    // Submit the scene for upload if two valid files are selected
    if (panchromatic && multiSpectral && date && !(panchromaticError || multiSpectralError)) {
      setIsSelecting(false);
      setError("");
      sha256(new TextEncoder().encode(`${multiSpectral.name}:${panchromatic.name}`))
        .then((key) => API.createScene(key, date))
        .then(({ properties }) => onSelect({
          key: properties.s3_prefix,
          date,
          panchromatic,
          multiSpectral
        }))
        .catch((e) => setError(e.message));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panchromatic, multiSpectral, date]);

  useEffect(() => {
    if (isSelecting) {
      setPanchromatic(undefined);
      setMultiSpectral(undefined);
      setDate("");
    }
  }, [isSelecting]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(e.target.checkValidity()) {
      setDate(new Date(e.target.value).toISOString());
    }
  };
  return (
    <>
      <ModalBody>
        <Text as="h3" mb="4">Select Files</Text>
        <FileList files={files} />
        {isSelecting ? (
          <Box display="flex" flexDirection="column" gap="6">
            <Text>Each scene requires a multi-spectral and panchromatic image. Select the appropriate files below.</Text>
            <Box>
              <DropZone handleFileDrop={setMultiSpectral}>
                <Text fontWeight="semibold">Multi-spectral image</Text>
                <Text fontSize="small" opacity="0.8">File must be a TIFF with maximum size of 20GB.</Text>
              </DropZone>
              {multiSpectralError && <Text mt="2" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}>{multiSpectralError}</Text>}
            </Box>

            <Box>
              <DropZone handleFileDrop={setPanchromatic}>
                <Text fontWeight="semibold">Panchromatic image</Text>
                <Text fontSize="small" opacity="0.8">File must be a TIFF with maximum size of 20GB.</Text>
              </DropZone>
              {panchromaticError && <Text mt="2" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}>{panchromaticError}</Text>}
            </Box>
            { panchromatic && multiSpectral && !date && (
              <FormControl>
                <FormLabel fontWeight="semibold">Set scene collection date</FormLabel>
                <Input
                  type="datetime-local"
                  required
                  min={MIN_DATE.toISOString()}
                  max={MAX_DATE.toISOString()}
                  value={date}
                  onChange={(e) => handleDateChange(e)}
                />
                <FormHelperText>The scene collection date cannot be inferred from the file names, or the file dates do not match</FormHelperText>
              </FormControl>
            )}
          </Box>
        ) : (
          <Button
            mt="4"
            w="100%"
            variant="base-outline"
            onClick={() => setIsSelecting(true)}
            leftIcon={<CollecticonPlusSmall />}
          >
            Upload another scene
          </Button>
        )}
        {error && <Text mt="4" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}><strong>Error:</strong> {error}</Text>}
      </ModalBody>
      <ModalFooter display="flex" gap="5">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleNext}>Next</Button>
      </ModalFooter>
    </>
  );
}

export default SelectFiles;
